export interface AttributionContext {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  gclid?: string
  fbclid?: string
}

const ATTRIBUTION_STORAGE_KEY = 'analytics_attribution_context_v1'
const ATTRIBUTION_TTL_MS = 1000 * 60 * 60 * 24 * 30 // 30 days

const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
] as const

type AttributionKey = (typeof ATTRIBUTION_KEYS)[number]

interface StoredAttributionContext {
  capturedAt: number
  expiresAt: number
  values: AttributionContext
}

function isBrowser() {
  return typeof window !== 'undefined'
}

function sanitizeValue(value: string | null): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function parseFromSearch(search: string): AttributionContext {
  const params = new URLSearchParams(search)
  const result: AttributionContext = {}

  ATTRIBUTION_KEYS.forEach((key) => {
    const value = sanitizeValue(params.get(key))
    if (value) {
      result[key] = value
    }
  })

  return result
}

function hasAttributionValues(context: AttributionContext): boolean {
  return ATTRIBUTION_KEYS.some((key) => Boolean(context[key]))
}

function mergeAttributionContexts(
  base: AttributionContext,
  override: AttributionContext,
): AttributionContext {
  const merged: AttributionContext = { ...base }
  ATTRIBUTION_KEYS.forEach((key) => {
    const value = sanitizeValue(override[key] ?? null)
    if (value) {
      merged[key] = value
    }
  })
  return merged
}

function readStoredPayload(): StoredAttributionContext | null {
  if (!isBrowser()) return null
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredAttributionContext
    if (!parsed?.expiresAt || parsed.expiresAt <= Date.now()) {
      window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeStoredPayload(values: AttributionContext): void {
  if (!isBrowser()) return
  if (!hasAttributionValues(values)) return
  const now = Date.now()
  const payload: StoredAttributionContext = {
    capturedAt: now,
    expiresAt: now + ATTRIBUTION_TTL_MS,
    values,
  }
  try {
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // no-op
  }
}

export function getStoredAttributionContext(): AttributionContext {
  return readStoredPayload()?.values ?? {}
}

export function captureAttributionFromSearch(search?: string): AttributionContext {
  if (!isBrowser()) return {}
  const fromSearch = parseFromSearch(search ?? window.location.search)
  const merged = mergeAttributionContexts(getStoredAttributionContext(), fromSearch)
  writeStoredPayload(merged)
  return merged
}

export function getAttributionContext(search?: string): AttributionContext {
  const fromSearch =
    isBrowser() && typeof search === 'string'
      ? parseFromSearch(search)
      : isBrowser()
        ? parseFromSearch(window.location.search)
        : {}
  return mergeAttributionContexts(getStoredAttributionContext(), fromSearch)
}

export function withAttributionContext<T extends Record<string, unknown>>(
  payload: T,
  search?: string,
): T & AttributionContext {
  const attribution = getAttributionContext(search)
  if (!hasAttributionValues(attribution)) {
    return payload as T & AttributionContext
  }
  return {
    ...payload,
    ...attribution,
  }
}

